import type { StatusRow } from '../ability/context';
import { readItemField } from '../compile/item-field-reader';
import { statusRows } from '../statuses/expiry';
import { ftClockCampaignField, ftClockColsFromArr } from '../../shared/campaign-clock';
import contract from '../../../catalogs/janus/row-contract.json';
import type { AppliedRow, AppliedTouchedRow } from './applied-frame';

const parts = ['year', 'month', 'day', 'hour', 'minute', 'second'] as const;
const required = ['status_id', 'status_item_id', 'status_target_id', 'status_caster_id'];

/** Routing identities distinguish an insert from an incomplete partial update.
 * Native A1/marker inserts omit unused nullable columns; compiler readers already
 * supply their absent-value defaults without recovering the held creature. */
export const savedStatusRow = (row: AppliedRow): boolean => required.every(key => Object.prototype.hasOwnProperty.call(row, key));

/** The decided end stamp has the same column shape in saved rows and held projections. */
export function statusEndFields(ends: string): AppliedRow {
    const values = ends.split('-').map(Number);
    return Object.fromEntries(parts.map((part, index) => ['status_ends_campaign_' + part, values[index] ?? 0]));
}

/** Serialize the status insert plan sent by ability/attack commits into its saved
 * row shape. No rule is evaluated here. Campaign and calendar use the same held
 * readers as the action; absent context leaves recovery to the receiving runtime. */
export function appliedStatusRows(added: readonly StatusRow[], campaign: AppliedRow | null,
    stacks: readonly { status_id: string; status_stack: number; target?: string }[] = [], members: readonly AppliedRow[] = []): AppliedTouchedRow[] {
    const rows: AppliedTouchedRow[] = [];
    if (campaign) for (const status of added) {
        if (!status.status_id) continue;
        const draft = status as unknown as AppliedRow;
        const row: AppliedRow = Object.fromEntries(contract.statuses.columns.map(key => [key, readItemField(draft, key) ?? null]));
        for (const key of contract.statuses.numbers) if (row[key] === null && !key.includes('_campaign_') && !key.startsWith('status_ends_on_')) row[key] = 0;
        Object.assign(row, { status_private: Number(readItemField(draft, 'status_private') ?? 0), status_deleted: 0, status_target_id: status.target, status_caster_id: status.caster,
            status_campaign_id: ftClockCampaignField(campaign, 'campaign_id') ?? null,
            status_casting_targets_arr: status.status_casting_targets_arr || null, status_origin_id: status.status_origin_id || null });
        const clock = ftClockColsFromArr(campaign);
        Object.assign(row, statusEndFields(status.ends));
        for (const part of parts) row['status_starts_campaign_' + part] = clock[part];
        rows.push({ table: 'statuses', row_id: status.status_id.toUpperCase(), row });
    }
    for (const stack of stacks) {
        const row = members.flatMap(member => {
            const envelope = member._entity_base as { base?: AppliedRow } | undefined;
            return [...statusRows(envelope?.base ?? null, 'target'), ...statusRows(envelope?.base ?? null, 'caster')];
        }).find(row => String(readItemField(row, 'status_id')).toUpperCase() === stack.status_id.toUpperCase());
        if (row) rows.push({ table: 'statuses', row_id: stack.status_id.toUpperCase(), row: { ...row, status_stack: stack.status_stack } });
        else if (stack.target) rows.push({ table: 'statuses', row_id: stack.status_id.toUpperCase(), partial: true,
            row: { status_id: stack.status_id, status_target_id: stack.target, status_stack: stack.status_stack } });
    }
    return rows;
}
